import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { HttpHeaders, HttpErrorResponse, HttpClient } from "@angular/common/http";
import { catchError, take } from "rxjs/operators";
import { throwError, Observable, firstValueFrom } from "rxjs";
import { GlobalService } from "../shared/global.service";
import { StorageService } from "./storage.service";
import { FireService } from "./fire.service";
import { LicenzeDataService } from "./licenze-data.service";
import { User } from "../entities/users";
@Injectable()
export class AuthClienteService {
	private baseUrl: string;
	constructor(
		private myRoute: Router,
		private http: HttpClient,
		private g: GlobalService,
		private storage: StorageService,
		private fire: FireService,
		private licenzeData: LicenzeDataService,
	) {
		this.baseUrl = this.g.nodePath;
	}

	// ######################################
	// FUNCTION: saveLoginResponse
	saveLoginResponse(response: { user: User; token: string }) {
		this.saveUser(response.user);
		this.storage.saveToken(response.token);
		this.storage.saveApiToken(response.token);
	}

	// ######################################
	// FUNCTION: saveUser
	// SECURITY: strip password before persisting. localStorage per persistenza
	// sessione mobile (chiusura browser non logga out). JWT 31d gestisce scadenza.
	saveUser(user: User) {
		const { pwd, temp_code, scadenza_temp_code, ...safeUser } = user as any;
		localStorage.setItem("LoggedInCustomer", btoa(JSON.stringify(safeUser)));
		// Compat retro per codice legacy che ancora legge da sessionStorage.
		sessionStorage.setItem("LoggedInCustomer", btoa(JSON.stringify(safeUser)));
	}

	// ######################################
	// FUNCTION: getUser
	getUser(): User | null {
		const loggedIn = localStorage.getItem("LoggedInCustomer") || sessionStorage.getItem("LoggedInCustomer");
		if (!loggedIn) return null;
		try {
			return JSON.parse(atob(loggedIn)) as User;
		} catch {
			localStorage.removeItem("LoggedInCustomer");
			sessionStorage.removeItem("LoggedInCustomer");
			return null;
		}
	}

	// ######################################
	// FUNCTION: resetUser
	// Rimozione targetizzata: evitiamo di cancellare localStorage per intero
	// (conteneva SelectedQuest/AmajorCompiledQuest usati dal flow compilazione).
	// Pulisci ANCHE `token` (magic-link base64): senza questo, al redirect su
	// /login, ngOnInit lo rilegge → auto-login → logout diventa ciclo infinito.
	resetUser() {
		localStorage.removeItem("LoggedInCustomer");
		sessionStorage.removeItem("LoggedInCustomer");
		sessionStorage.removeItem("CustomerLogo");
		this.storage.saveToken('');
		this.storage.clearApiToken();
		// Snapshot utente legacy (StorageService.setUser). Preferenze non
		// personali (lingua, layout) restano.
		localStorage.removeItem("user");
		// Cache in memoria (injector root): la SPA non ricarica la pagina, quindi
		// senza questo un login successivo nella stessa tab (anche di un altro
		// utente) ripartiva dalle licenze e dai titoli del precedente.
		this.licenzeData.clear();
	}

	// ######################################
	// FUNCTION: isLoggedIn
	// Richiede LoggedInCustomer + apiToken presente e non scaduto (fail-closed in
	// StorageService.isApiTokenExpired): altrimenti pulisce lo storage e ritorna
	// false (il guard farà il redirect al login).
	isLoggedIn() {
		if (this.getUser() === null) return false;
		if (this.storage.isApiTokenExpired()) {
			this.resetUser();
			return false;
		}
		return true;
	}

	/**
	 * Ri-allinea la copia locale dell'utente col doc Firestore.
	 *
	 * `LoggedInCustomer` è uno snapshot scritto al login e il JWT dura 31 giorni:
	 * senza questo refresh `descrizione`, `titolare`, `permessi`, `attivo` e
	 * `lingua` restavano congelati per un mese e le modifiche dell'admin erano
	 * visibili solo dopo un logout. Preserva i campi di sessione (`deadline`,
	 * `azienda`/`reparti`/`utenti` del magic link) che non esistono nel doc.
	 *
	 * @returns `false` se l'utente è stato disattivato lato admin (il chiamante
	 *          deve forzare il logout), `true` altrimenti.
	 */
	async refreshUser(): Promise<boolean> {
		const local: any = this.getUser();
		// `reparti`/`utenti` arrivano dal payload del magic link; senza uno dei
		// segmenti il path Firestore avrebbe un segmento vuoto → `.doc('')`
		// lancia in modo sincrono. Niente path valido: nulla da rinfrescare.
		const azienda = local?.azienda;
		const reparto = local?.reparti ?? local?.reparto;
		const utente = local?.utenti ?? local?.user ?? local?.email;
		if (!azienda || !reparto || !utente) return true;

		try {
			const snap: any = await firstValueFrom(
				this.fire.getSubDocOnce("clienti", azienda, "reparti", reparto, "utenti", utente).pipe(take(1))
			);
			const fresh = snap?.exists ? snap.data() : null;
			if (!fresh) return true;

			// Utente disattivato dall'admin: la sessione non deve sopravvivere.
			if (fresh.attivo === false) return false;

			// Merge: il doc vince sui campi anagrafici/permessi, i campi di
			// sessione assenti nel doc restano quelli locali.
			this.saveUser({ ...local, ...fresh });
			return true;
		} catch (err) {
			// Offline o rules: si continua con lo snapshot locale.
			console.debug("refreshUser error:", err);
			return true;
		}
	}

	// ######################################
	// FUNCTION: logout
	logout() {
		this.resetUser();
		this.myRoute.navigate(["login"]);
		this.g.actualPage = "login";
	}

	// ######################################
	// FUNCTION: checkLogin
	checkLogin(formData: any): Observable<Error | any> {
		const header = new HttpHeaders();
		header.append("Content-Type", "application/json");

		return this.http.post<any>(this.baseUrl + "/clienti/login", formData).pipe(
			catchError((err: HttpErrorResponse) => {
				if (err.error instanceof Error) {
					// A client-side or network error occurred
					const details = { detail: err.error, status: err.status };
					return throwError(details);
				} else {
					// A backend error occured (err.status = 500)
					const details = { detail: err.error, status: err.status };
					return throwError(details);
				}
			})
		);
	}

	// ######################################
	// FUNCTION: saveLogo
	saveLogo(logo: string) {
		sessionStorage.setItem("CustomerLogo", JSON.stringify(logo));
	}

	// ######################################
	// FUNCTION: getLogo
	getLogo() {
		let loggedIn = sessionStorage.getItem("CustomerLogo") as string;
		return JSON.parse(loggedIn);
	}
}
